import React, { useEffect, useRef, useState } from "react";
import api, { apiErr, API } from "../lib/api";
import { PageHeader, fmtNum } from "../components/Shared";
import { toast } from "sonner";
import {
  Search, Upload, Download, Plus, Trash2, FileSpreadsheet, Loader2, X, ChevronLeft, ChevronRight,
} from "lucide-react";

const PAGE = 50;

export default function MasterObat() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [importMode, setImportMode] = useState("replace");
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  const load = async (p = page, s = search) => {
    setLoading(true);
    try {
      const { data } = await api.get("/medicines", { params: { search: s, skip: p * PAGE, limit: PAGE } });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(0, ""); }, []);

  useEffect(() => {
    const t = setTimeout(() => { setPage(0); load(0, search); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const doImport = async () => {
    if (!file) { toast.error("Pilih file Excel dulu"); return; }
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/medicines/import?mode=${importMode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.imported} obat berhasil diimpor`);
      setShowImport(false);
      setFile(null);
      load(0, "");
      setSearch("");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/template/download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Template_Stock_Opname.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengunduh template"); }
  };

  const clearAll = async () => {
    if (!window.confirm("Hapus SEMUA master obat? Tindakan ini tidak dapat dibatalkan.")) return;
    try {
      await api.delete("/medicines");
      toast.success("Semua master obat dihapus");
      load(0, "");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus obat ini?")) return;
    await api.delete(`/medicines/${id}`);
    toast.success("Obat dihapus");
    load();
  };

  const totalPages = Math.ceil(total / PAGE);

  return (
    <div className="pb-12">
      <PageHeader title="Master Obat" subtitle={`${fmtNum(total)} item obat terdaftar`}>
        <button onClick={downloadTemplate} data-testid="download-template-button" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
          <Download className="h-4 w-4" /> Template
        </button>
        <button onClick={() => setShowAdd(true)} data-testid="add-medicine-button" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
          <Plus className="h-4 w-4" /> Tambah
        </button>
        <button onClick={() => setShowImport(true)} data-testid="import-excel-button" className="inline-flex items-center gap-2 rounded-lg bg-teal-700 hover:bg-teal-800 px-3.5 py-2 text-sm font-semibold text-white transition-colors">
          <Upload className="h-4 w-4" /> Impor Excel
        </button>
      </PageHeader>

      <div className="px-6 lg:px-8 pt-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="search-medicine-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari nama / ID barang / ID inventory..."
              className="w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600 transition-colors"
            />
          </div>
          {total > 0 && (
            <button onClick={clearAll} data-testid="clear-all-button" className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors ml-auto">
              <Trash2 className="h-4 w-4" /> Hapus Semua
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {["Nama Obat", "ID Barang", "Produsen", "Gudang", "Lokasi Penyimpanan", "Satuan", "Qty Awal", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-16 text-center">
                    <FileSpreadsheet className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">Belum ada data. Impor master obat via Excel.</p>
                  </td></tr>
                ) : items.map((m) => (
                  <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors" data-testid="medicine-row">
                    <td className="px-4 py-3 font-medium text-slate-800 max-w-md">{m.nama}</td>
                    <td className="px-4 py-3 font-mono-t text-xs text-slate-500">{m.id_barang || "-"}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{m.produsen || "-"}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{m.gudang || "-"}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{m.lokasi_penyimpanan || "-"}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs uppercase">{m.satuan_jual || "-"}</td>
                    <td className="px-4 py-3 text-right tabular font-mono-t text-slate-800">{fmtNum(m.quantity_awal)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => del(m.id)} className="text-slate-400 hover:text-red-600 transition-colors" data-testid="delete-medicine-button"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Halaman {page + 1} dari {totalPages}</span>
              <div className="flex gap-2">
                <button disabled={page === 0} onClick={() => { const p = page - 1; setPage(p); load(p); }} className="p-1.5 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
                <button disabled={page + 1 >= totalPages} onClick={() => { const p = page + 1; setPage(p); load(p); }} className="p-1.5 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showImport && (
        <Modal onClose={() => setShowImport(false)} title="Impor Master Obat (Excel)">
          <p className="text-sm text-slate-500 mb-4">
            Unggah file <b>.xlsx</b> dengan header sesuai template (ID Inventory, ID Barang, Nama, Produsen, Gudang, Lokasi penyimpanan, Harga Pokok, Satuan Jual, Quantity Awal).
          </p>
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-teal-500 hover:bg-teal-50/30 transition-colors"
          >
            <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600 font-medium">{file ? file.name : "Klik untuk memilih file Excel"}</p>
            <input ref={fileRef} data-testid="upload-file-input" type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
          </div>

          <div className="mt-4 space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="radio" checked={importMode === "replace"} onChange={() => setImportMode("replace")} /> Ganti semua data lama
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="radio" checked={importMode === "append"} onChange={() => setImportMode("append")} /> Tambahkan ke data yang ada
            </label>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button onClick={() => setShowImport(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">Batal</button>
            <button onClick={doImport} disabled={importing} data-testid="confirm-import-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold disabled:opacity-60">
              {importing && <Loader2 className="h-4 w-4 animate-spin" />} Impor Sekarang
            </button>
          </div>
        </Modal>
      )}

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(0, ""); }} />}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AddModal({ onClose, onSaved }) {
  const [f, setF] = useState({ nama: "", id_barang: "", produsen: "", gudang: "", lokasi_penyimpanan: "", satuan_jual: "", harga_pokok: 0, quantity_awal: 0 });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async () => {
    if (!f.nama.trim()) { toast.error("Nama obat wajib diisi"); return; }
    setSaving(true);
    try {
      await api.post("/medicines", f);
      toast.success("Obat ditambahkan");
      onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const field = (label, k, type = "text", span = false) => (
    <div className={span ? "col-span-2" : ""}>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input value={f[k]} type={type} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`add-${k}-input`}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600" />
    </div>
  );

  return (
    <Modal title="Tambah Obat Manual" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        {field("Nama Obat *", "nama", "text", true)}
        {field("ID Barang", "id_barang")}
        {field("Produsen", "produsen")}
        {field("Gudang", "gudang")}
        {field("Lokasi Penyimpanan", "lokasi_penyimpanan")}
        {field("Satuan Jual", "satuan_jual")}
        {field("Harga Pokok", "harga_pokok", "number")}
        {field("Quantity Awal", "quantity_awal", "number")}
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">Batal</button>
        <button onClick={save} disabled={saving} data-testid="save-medicine-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold disabled:opacity-60">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
        </button>
      </div>
    </Modal>
  );
}
